import { Spacing } from '@persona-id/design-tokens';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { DismissButton } from '@react-aria/overlays';
import {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ComponentType,
  ElementType,
  FC,
  forwardRef,
  ReactNode,
  Ref,
  RefObject,
} from 'react';
import styled, {
  css,
  DefaultTheme,
  StyledComponent,
  StyledComponentPropsWithRef,
  ThemeProvider,
} from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import Card from 'design-system/components/Card';
import { isDisabled, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import menuItemMixins from 'design-system/mixins/menuItem';
import { paragraphMixins } from 'design-system/mixins/text';
import { darkTheme } from 'design-system/theme';
import { FontWeight } from 'design-system/variables/text';

export const FloatingCard: StyledComponent<
  ComponentType<StyledComponentPropsWithRef<typeof Card>>,
  DefaultTheme
> = styled(Card)`
  box-shadow: 0px 0px 14px 2px rgba(0, 0, 0, 0.12);
  outline: none;
  z-index: ${Z_INDICES.tooltip};
`;

export const MenuList = styled.div.attrs<{ padding?: Spacing }>(({ role }) => ({
  role: role ?? 'menu',
}))<{ padding?: Spacing }>`
  list-style: none;
  margin: 0;
  padding: ${({ padding }) => padding || Spacing[2]};
  outline: none;
`;

export interface MenuItemProps<T extends ElementType = 'div'> {
  children: ReactNode;
  prefixIcon?: ReactNode;
  suffixIcon?: ReactNode;
  disabled?: boolean;
  variant?: 'basic' | 'danger';
  as?: T;
  stretchLabel?: boolean;
  subtitle?: string;
  focused?: boolean;
}

export const ListItem = styled.li<Pick<MenuItemProps, 'variant' | 'disabled' | 'focused'>>`
  ${paragraphMixins.md};
  display: flex;
  align-items: center;
  padding: ${Spacing[2]};
  border-radius: 4px;
  list-style: none;
  text-decoration: none;
  color: ${({ theme }) => theme.color.text.primary};
  cursor: pointer;
  ${({ focused, theme }) =>
    focused &&
    css`
      outline: none;
      color: ${theme.color.text.brand};
      background-color: ${theme.color.background.highlight};
    `}

  &${isNotDisabled}${isHovered}, &${isNotDisabled}${isFocused} {
    ${menuItemMixins.hover};
    outline: none;
    color: ${({ theme }) => theme.color.text.brand};
    background-color: ${({ theme }) => theme.color.background.highlight};
  }

  &${isDisabled} {
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: default;
    pointer-events: none;
  }

  /* Variant Styling */
  ${({ theme, variant }) =>
    variant === 'danger' &&
    css`
      color: ${theme.color.variants[variant].default};

      &${isNotDisabled}${isHovered}, &${isNotDisabled}${isFocused} {
        color: ${theme.color.variants[variant].default};
        background-color: ${theme.color.variants[variant].highlightLight};
      }

      &${isDisabled} {
        color: ${({ theme }) => theme.color.variants[variant].disabled};
      }
    `}
`;

const IconContainer = styled('div')<{ suffix?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: ${Spacing[2]};
  line-height: 1;

  ${({ suffix }) =>
    suffix &&
    css`
      margin-left: ${Spacing[2]};
    `}
`;

const LabelContainer = styled('div')<{ stretchLabel: boolean }>`
  margin-bottom: -1px;
  line-height: 16px;
  ${({ stretchLabel }) =>
    stretchLabel &&
    css`
      width: 100%;
    `}
`;

/**
 * https://stackoverflow.com/a/73795494
 */
interface MenuItemType<T extends ElementType = 'div'>
  extends FC<MenuItemProps<T> & ComponentPropsWithRef<T>> {
  <T extends ElementType = 'div'>(
    props: MenuItemProps<T> & ComponentPropsWithoutRef<T>,
  ): ReturnType<FC<MenuItemProps<T> & ComponentPropsWithoutRef<T>>>;
}

export const MenuItem: MenuItemType = forwardRef(function MenuItem(
  { children, disabled, variant, prefixIcon, suffixIcon, stretchLabel, ...elemProps },
  ref: Ref<HTMLDivElement>,
) {
  return (
    <ListItem
      disabled={disabled}
      variant={variant}
      ref={ref}
      aria-disabled={disabled}
      role='menuitem'
      tabIndex={disabled ? -1 : 0}
      {...elemProps}
    >
      {prefixIcon && <IconContainer>{prefixIcon}</IconContainer>}
      <LabelContainer stretchLabel={!!stretchLabel}>{children}</LabelContainer>
      {suffixIcon && <IconContainer suffix>{suffixIcon}</IconContainer>}
    </ListItem>
  );
});

const StyledMenuDivider = styled.hr`
  height: 0px;
  padding: 0px;
  font-size: 0;
  margin: ${Spacing[2]} -${Spacing[2]};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
`;

export const MenuDivider = (props: ComponentPropsWithoutRef<'hr'>) => (
  <DropdownMenu.Separator asChild>
    <StyledMenuDivider {...props} />
  </DropdownMenu.Separator>
);

export const MenuHeading = styled.li`
  ${paragraphMixins.sm};
  text-transform: uppercase;
  padding: ${Spacing[2]};
  display: block;
  color: ${({ theme }) => theme.color.text.secondary};
  font-weight: ${FontWeight.Regular};
  letter-spacing: 0.1em;
`;

export interface MenuProps<T extends ElementType = 'div'> {
  children?: ReactNode;
  className?: string;
  variant?: 'light' | 'dark';
  as?: T;
  containerProps?: ComponentPropsWithRef<'div'>;
  listRef?: RefObject<T>;
  itemProps?:
    | ComponentPropsWithoutRef<'li'>
    | ((
        props: ComponentPropsWithoutRef<'li'>,
        index: number,
      ) => Partial<ComponentPropsWithRef<'li'>>);
  onClose?: VoidFunction;
}

const Menu = <T extends ElementType = 'div'>({
  children,
  variant,
  containerProps,
  itemProps,
  listRef,
  className,
  onClose,
  as,
  ...props
}: MenuProps<T> & ComponentPropsWithoutRef<T>) => {
  const menu = (
    <FloatingCard {...containerProps} className={className}>
      <MenuList {...props} as={as as any} ref={listRef}>
        {children}
        <DismissButton onDismiss={onClose} />
      </MenuList>
    </FloatingCard>
  );

  if (variant === 'dark') {
    return <ThemeProvider theme={darkTheme}>{menu}</ThemeProvider>;
  }

  return menu;
};

export default Menu;
